/**
 * 家系図SVG生成ユーティリティ
 *
 * @module renderFamilyTree
 */

/**
 * PNGアイコンのURL設定
 */
const ICON_URLS = {
  deceased: '/build/media/family-tree-icons/deceased.png',    // 被相続人
  spouse: '/build/media/family-tree-icons/spouse.png',        // 配偶者
  child: '/build/media/family-tree-icons/child.png',          // 子
  grandchild: '/build/media/family-tree-icons/grandchild.png', // 孫
  father: '/build/media/family-tree-icons/father.png',        // 父
  mother: '/build/media/family-tree-icons/mother.png',        // 母
  sibling: '/build/media/family-tree-icons/sibling.png'       // 兄弟姉妹
};

/**
 * アイコンキャッシュ（Base64データURL）
 */
const iconCache = {};

/**
 * PNGアイコンを読み込んでBase64データURLに変換
 * @param {string} url - PNGファイルのURL
 * @returns {Promise<string>} - Base64データURL
 */
async function loadPNGIcon(url) {
  // キャッシュ済みの場合は即座に返す
  if (iconCache[url]) {
    return iconCache[url];
  }

  const response = await fetch(url);
  const blob = await response.blob();

  // BlobをBase64データURLに変換
  const dataUrl = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  // キャッシュに保存
  iconCache[url] = dataUrl;

  return dataUrl;
}

/**
 * 全てのアイコンを事前読み込み
 * @returns {Promise<void>}
 */
export async function preloadIcons() {
  const urls = Object.values(ICON_URLS);
  await Promise.all(urls.map(url => loadPNGIcon(url)));
}

/**
 * 家系図SVGを生成する
 *
 * @param {Object} familyData - 家族構成データ
 * @param {string|Object} familyData.spouse - 配偶者の状態 ('alive' | 'deceased' | 'none') または { status: 'alive', ratio: '1/2' }
 * @param {number} familyData.childrenCount - 子供の人数
 * @param {Array<Object>} familyData.children - 子供の配列
 * @param {string} familyData.children[].status - 子供の状態 ('alive' | 'deceased')
 * @param {number} familyData.children[].grandchildren - 孫の人数
 * @param {string} [familyData.children[].ratio] - 子供の相続割合（例: '1/4'）
 * @param {string} [familyData.children[].grandchildRatio] - 孫の相続割合（例: '1/8'）
 * @param {number} containerWidth - コンテナ幅
 * @param {Object} [options] - オプション設定（省略可）
 * @param {boolean} [options.isMobile] - モバイル判定（省略時は自動判定）
 * @param {boolean} [options.showRatios=false] - 相続割合を表示するか
 *
 * @returns {Promise<SVGElement>} - 生成されたSVG要素
 *
 * @example
 * // シンプルな家族構成: 配偶者（健在）+ 子供2人（両方健在）
 * const familyData1 = {
 *   spouse: 'alive',
 *   childrenCount: 2,
 *   children: [
 *     { status: 'alive', grandchildren: 0 },
 *     { status: 'alive', grandchildren: 0 }
 *   ]
 * };
 * const svg1 = await renderFamilyTree(familyData1, 800);
 * // → 被相続人（グレー） - 配偶者（オレンジ）
 * //              |
 * //         子1（ブルー） 子2（ブルー）
 *
 * @example
 * // 代襲相続: 配偶者（健在）+ 子供2人（1人故人・孫3人）
 * const familyData2 = {
 *   spouse: 'alive',
 *   childrenCount: 2,
 *   children: [
 *     { status: 'deceased', grandchildren: 3 },
 *     { status: 'alive', grandchildren: 0 }
 *   ]
 * };
 * const svg2 = await renderFamilyTree(familyData2, 800);
 * // → 被相続人（グレー） - 配偶者（オレンジ）
 * //              |
 * //         子1（グレー） 子2（ブルー）
 * //              |
 * //          孫3名（グリーン）
 *
 * @example
 * // 配偶者なし: 子供3人（2人故人・孫あり）
 * const familyData3 = {
 *   spouse: 'none',
 *   childrenCount: 3,
 *   children: [
 *     { status: 'deceased', grandchildren: 2 },
 *     { status: 'alive', grandchildren: 0 },
 *     { status: 'deceased', grandchildren: 1 }
 *   ]
 * };
 * const svg3 = await renderFamilyTree(familyData3, 800);
 * // →      被相続人（グレー）
 * //              |
 * //    子1（グレー） 子2（ブルー） 子3（グレー）
 * //         |                    |
 * //     孫2名（グリーン）       孫1名（グリーン）
 *
 * @example
 * // 相続割合を表示: 配偶者（健在）+ 子供2人（両方健在）
 * const familyData4 = {
 *   spouse: { status: 'alive', ratio: '1/2' },
 *   childrenCount: 2,
 *   children: [
 *     { status: 'alive', grandchildren: 0, ratio: '1/4' },
 *     { status: 'alive', grandchildren: 0, ratio: '1/4' }
 *   ]
 * };
 * const svg4 = await renderFamilyTree(familyData4, 800, { showRatios: true });
 * // → 被相続人 - 配偶者 (1/2)
 * //         |
 * //   子1 (1/4) 子2 (1/4)
 *
 * @example
 * // 相続割合を表示: 代襲相続のケース
 * const familyData5 = {
 *   spouse: { status: 'alive', ratio: '1/2' },
 *   childrenCount: 2,
 *   children: [
 *     { status: 'deceased', grandchildren: 2, grandchildRatio: '1/8' },
 *     { status: 'alive', grandchildren: 0, ratio: '1/4' }
 *   ]
 * };
 * const svg5 = await renderFamilyTree(familyData5, 800, { showRatios: true });
 * // → 被相続人 - 配偶者 (1/2)
 * //         |
 * //     子1    子2 (1/4)
 * //      |
 * // 孫2名 (1/8)
 */
export default async function renderFamilyTree(familyData, containerWidth, options = {}) {
  // renderFamilyTree専用の定数
  const MOBILE_BREAKPOINT = 500;  // この幅以下で縦並びレイアウトに切り替え
  const TEXT_COLORS = {
    ALIVE: '#333333',    // テキスト（健在）
    DECEASED: '#666666'  // テキスト（故人）
  };

  const isMobile = options.isMobile !== undefined ? options.isMobile : (containerWidth <= MOBILE_BREAKPOINT);
  const showRatios = options.showRatios || false;  // 相続割合の表示フラグ
  const hasSpouse = familyData.spouse !== 'none';  // 配偶者の有無

  // 相続順位の判定
  const { father, mother, siblingsCount = 0, children = [] } = familyData;
  const hasFirstRankHeirs = children.some(c => c.status === 'alive' || (c.status === 'deceased' && c.grandchildren > 0));
  const hasSecondRankHeirs = father === 'alive' || mother === 'alive';
  const hasThirdRankHeirs = siblingsCount > 0;

  let activeRank;
  if (hasFirstRankHeirs) {
    activeRank = 1;
  } else if (hasSecondRankHeirs) {
    activeRank = 2;
  } else if (hasThirdRankHeirs) {
    activeRank = 3;
  } else {
    activeRank = 0; // 配偶者のみ or 相続人なし
  }

  // 画面サイズに応じてアイコンサイズとフォントサイズを調整
  const iconScale = isMobile ? 0.25 : 0.5;
  const fontSize = isMobile ? 11 : 18;
  const ratioFontSize = fontSize;  // 相続割合のフォントサイズ（名前と同じ）

  // アイコンの実際の寸法
  const iconHeight = 128 * iconScale;
  const iconWidth = 128 * iconScale;
  const textBottomGap = fontSize * 0.3; // アイコンとテキストの間隔

  // レイアウト定数
  const coupleGap = iconWidth * 2.5 * 0.15; // 被相続人と配偶者の間隔
  const lineIconGap = iconHeight * 0.1;
  const grandchildConnectorGap = iconHeight * 0.3;

  // 間隔を寸法から計算
  const verticalGap = (iconHeight + fontSize + textBottomGap) * 1.0; // 被相続人から子供への垂直間隔

  // SVGの幅はコンテナ幅に合わせる
  const width = containerWidth;

  // 高さを動的に計算（実際のノード配置に基づく）
  const topMargin = iconHeight / 2; // アイコンの上半分のスペース（アイコンは中心座標で描画されるため）
  const bottomMargin = 0; // 下部余白なし（外側のコンテナでpaddingを設定）

  // 第2順位（父母）の場合は上に父母を配置するため、被相続人の開始位置をずらす
  // 縦線が見えるように十分な間隔を確保
  const parentsRowHeight = activeRank === 2 ? verticalGap * 1.5 : 0;

  // ノード配置後、全ノードの最大Y座標を計算
  // 被相続人・配偶者の下端を初期値として設定
  let maxY = topMargin + parentsRowHeight + iconHeight / 2 + fontSize + textBottomGap;

  // 相続割合表示時は配偶者の割合分のスペースも追加
  if (showRatios && hasSpouse) {
    maxY += ratioFontSize + textBottomGap;
  }

  // 相続人のY座標を考慮（順位に応じて子供/兄弟を表示 - 父母は上に配置済み）
  // 第3順位（兄弟）は被相続人と同じ行に配置するため、下に表示するのは第1順位のみ
  const hasHeirsBelow = activeRank === 1; // 第1順位のみ下に表示
  if (hasHeirsBelow) {
    const heirsY = topMargin + parentsRowHeight + iconHeight / 2 + fontSize + textBottomGap + verticalGap;
    const heirBottomY = heirsY + iconHeight / 2 + fontSize + textBottomGap;
    maxY = Math.max(maxY, heirBottomY);

    // 相続割合表示時は追加スペース
    if (showRatios) {
      maxY += ratioFontSize + textBottomGap;
    }

    // 孫がいる場合はさらに下に伸びる（第1順位のみ）
    if (activeRank === 1) {
      familyData.children.forEach(child => {
        if (child.grandchildren > 0) {
          const grandchildrenY = heirsY + iconHeight + fontSize + textBottomGap + grandchildConnectorGap * 2 + lineIconGap;
          const grandchildBottomY = grandchildrenY + iconHeight / 2 + fontSize + textBottomGap;
          maxY = Math.max(maxY, grandchildBottomY);

          // 相続割合表示時は追加スペース
          if (showRatios) {
            maxY += ratioFontSize + textBottomGap;
          }
        }
      });
    }
  }

  const height = maxY + bottomMargin;

  // SVGを作成（コンテナ幅に完全に収める）
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');

  // viewBoxで内部座標系を定義
  svg.setAttribute('viewBox', `0 0 ${width} ${height}`);

  // アスペクト比を維持して高さを自動計算
  svg.setAttribute('preserveAspectRatio', 'xMidYMin meet');

  // CSSで確実に収める（はみ出さないように）
  svg.style.width = '100%';
  svg.style.maxWidth = '100%';
  svg.style.height = 'auto';
  svg.style.display = 'block';

  let nodeId = 0;
  const nodes = [];

  // 被相続人と配偶者（中央配置で横並び）
  // 第2順位の場合は父母が上にいるため、被相続人の位置を下にずらす
  const coupleY = topMargin + parentsRowHeight;
  const personWidth = iconWidth * 2.5;

  // 第3順位（兄弟）の場合は左側にスペースが必要なため、中心を右にずらす
  const siblingOffset = (activeRank === 3 && siblingsCount > 0) ? personWidth * 0.7 : 0;
  const centerX = width / 2 + siblingOffset;

  // 被相続人
  const deceasedX = hasSpouse ? centerX - coupleGap / 2 - personWidth / 2 : centerX;
  nodes.push({
    id: nodeId++,
    key: 'deceased',  // 相続割合のキー
    label: '被相続人',
    x: deceasedX,
    y: coupleY,
    status: 'deceased',
    type: 'deceased'
  });

  // 配偶者
  let spouseX;
  if (hasSpouse) {
    spouseX = centerX + coupleGap / 2 + personWidth / 2;
    const spouseRatio = typeof familyData.spouse === 'object' ? familyData.spouse.ratio : undefined;
    nodes.push({
      id: nodeId++,
      key: 'spouse',  // 相続割合のキー
      label: '配偶者',
      x: spouseX,
      y: coupleY,
      status: typeof familyData.spouse === 'object' ? familyData.spouse.status : familyData.spouse,
      isHeir: (typeof familyData.spouse === 'object' ? familyData.spouse.status : familyData.spouse) === 'alive',
      type: 'spouse',
      ratio: spouseRatio
    });
  }

  // 相続人を横並びで配置（相続順位に応じて子供/父母/兄弟を表示）
  const heirsY = coupleY + iconHeight / 2 + fontSize + textBottomGap + verticalGap;
  const heirNodes = [];

  // 相続人の横間隔（相続割合表示時は広めに）
  const heirHorizontalGap = showRatios ? iconWidth * 1.0 : iconWidth * 0.3;

  // 第1順位: 子供・孫
  if (activeRank === 1) {
    const totalChildrenWidth = familyData.childrenCount * iconWidth + (familyData.childrenCount - 1) * heirHorizontalGap;
    const childrenStartX = centerX - totalChildrenWidth / 2 + iconWidth / 2;

    familyData.children.forEach((child, i) => {
      const childX = childrenStartX + i * (iconWidth + heirHorizontalGap);
      const childNode = {
        id: nodeId++,
        key: `child${i}`,
        label: `子${i + 1}`,
        x: childX,
        y: heirsY,
        status: child.status,
        isHeir: child.status === 'alive',
        type: 'child',
        parentIndex: i,
        ratio: child.ratio
      };
      nodes.push(childNode);
      heirNodes.push(childNode);

      // 孫がいる場合
      if (child.grandchildren > 0) {
        const grandchildrenY = heirsY + iconHeight + fontSize + textBottomGap + grandchildConnectorGap * 2 + lineIconGap;
        nodes.push({
          id: nodeId++,
          key: `grandchild${i}`,
          label: `孫${child.grandchildren}名`,
          x: childX,
          y: grandchildrenY,
          status: 'alive',
          isHeir: true,
          type: 'grandchild',
          parentIndex: i,
          count: child.grandchildren,
          ratio: child.grandchildRatio
        });
      }
    });
  }

  // 第2順位: 父母（被相続人の真上に配置）
  const parentNodes = []; // 関数スコープで宣言（線描画で参照）
  if (activeRank === 2) {
    const aliveParents = [];
    if (father === 'alive') aliveParents.push({ name: '父', type: 'father' });
    if (mother === 'alive') aliveParents.push({ name: '母', type: 'mother' });

    // 父母は被相続人の真上に配置（deceasedXを中心に）
    const parentsY = topMargin;
    const totalParentsWidth = aliveParents.length * iconWidth + (aliveParents.length - 1) * heirHorizontalGap;
    const parentsStartX = deceasedX - totalParentsWidth / 2 + iconWidth / 2;

    aliveParents.forEach((parent, i) => {
      const parentX = parentsStartX + i * (iconWidth + heirHorizontalGap);
      const parentNode = {
        id: nodeId++,
        key: parent.type,
        label: parent.name,
        x: parentX,
        y: parentsY,
        status: 'alive',
        isHeir: true,
        type: parent.type,
        ratio: familyData[`${parent.type}Ratio`]
      };
      nodes.push(parentNode);
      parentNodes.push(parentNode);
    });
  }

  // 第3順位: 兄弟姉妹（被相続人の左側に「兄弟N名」として1つにまとめて配置）
  let siblingNode = null;
  if (activeRank === 3 && siblingsCount > 0) {
    // 被相続人の左側に配置
    const siblingX = deceasedX - coupleGap - personWidth;

    // 兄弟全体の合計割合を取得
    const siblingTotalRatio = familyData.siblingTotalRatio || undefined;

    siblingNode = {
      id: nodeId++,
      key: 'siblings',
      label: `兄弟${siblingsCount}名`,
      x: siblingX,
      y: coupleY,  // 被相続人と同じ行
      status: 'alive',
      isHeir: true,
      type: 'sibling',
      ratio: siblingTotalRatio
    };
    nodes.push(siblingNode);
    // heirNodesには追加しない（被相続人の下に線を引かないため）
  }

  // 後方互換性のためchildNodesも維持
  const childNodes = heirNodes;

  // 線の描画グループを作成
  const linesGroup = document.createElementNS('http://www.w3.org/2000/svg', 'g');

  // 1. 被相続人と配偶者を結ぶ横線（アイコンの中央に配置）
  if (hasSpouse) {
    const coupleLineY = coupleY;
    const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', deceasedX + iconWidth / 2);
    line.setAttribute('y1', coupleLineY);
    line.setAttribute('x2', spouseX - iconWidth / 2);
    line.setAttribute('y2', coupleLineY);
    line.setAttribute('stroke', '#6a3e21');
    line.setAttribute('stroke-width', '1');
    linesGroup.appendChild(line);
  }

  // 1.5. 兄弟と被相続人を結ぶ横線（第3順位の場合）
  if (siblingNode) {
    const siblingLineY = coupleY;
    const siblingLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    siblingLine.setAttribute('x1', siblingNode.x + iconWidth / 2);
    siblingLine.setAttribute('y1', siblingLineY);
    siblingLine.setAttribute('x2', deceasedX - iconWidth / 2);
    siblingLine.setAttribute('y2', siblingLineY);
    siblingLine.setAttribute('stroke', '#6a3e21');
    siblingLine.setAttribute('stroke-width', '1');
    linesGroup.appendChild(siblingLine);
  }

  // 2. 父母から被相続人への縦線（第2順位の場合）
  if (activeRank === 2 && parentNodes.length > 0) {
    const parentsY = topMargin;
    // 横線のY座標（父母のアイコンの中央）
    const parentsLineY = parentsY;

    // 父母を結ぶ横線（2人いる場合）
    if (parentNodes.length > 1) {
      const parentsHorizontalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const firstParentX = parentNodes[0].x + iconWidth / 2;
      const lastParentX = parentNodes[parentNodes.length - 1].x - iconWidth / 2;
      parentsHorizontalLine.setAttribute('x1', firstParentX);
      parentsHorizontalLine.setAttribute('y1', parentsLineY);
      parentsHorizontalLine.setAttribute('x2', lastParentX);
      parentsHorizontalLine.setAttribute('y2', parentsLineY);
      parentsHorizontalLine.setAttribute('stroke', '#6a3e21');
      parentsHorizontalLine.setAttribute('stroke-width', '1');
      linesGroup.appendChild(parentsHorizontalLine);
    }

    // 父母から被相続人への縦線
    // 2人いる場合: 横線の位置から開始
    // 1人の場合: アイコン+テキストの下から開始（アイコンと重ならないように）
    const verticalLineStartY = parentNodes.length > 1
      ? parentsLineY
      : parentsY + iconHeight / 2 + fontSize + textBottomGap;

    const centerVerticalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    centerVerticalLine.setAttribute('x1', deceasedX);
    centerVerticalLine.setAttribute('y1', verticalLineStartY);
    centerVerticalLine.setAttribute('x2', deceasedX);
    centerVerticalLine.setAttribute('y2', coupleY - iconHeight / 2 - lineIconGap);
    centerVerticalLine.setAttribute('stroke', '#6a3e21');
    centerVerticalLine.setAttribute('stroke-width', '1');
    linesGroup.appendChild(centerVerticalLine);
  }

  // 3. 被相続人から相続人への垂直線（第1・第3順位の場合）
  if (heirNodes.length > 0) {
    const heirsLineY = heirsY - iconHeight / 2 - lineIconGap - grandchildConnectorGap;

    // 配偶者がいる場合は横線の位置から、いない場合は被相続人のテキストの下から開始
    const verticalLineStartY = hasSpouse ? coupleY : coupleY + iconHeight / 2 + fontSize + textBottomGap;

    const verticalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    verticalLine.setAttribute('x1', centerX);
    verticalLine.setAttribute('y1', verticalLineStartY);
    verticalLine.setAttribute('x2', centerX);
    verticalLine.setAttribute('y2', heirsLineY);
    verticalLine.setAttribute('stroke', '#6a3e21');
    verticalLine.setAttribute('stroke-width', '1');
    linesGroup.appendChild(verticalLine);

    // 4. 相続人を結ぶ横線
    if (heirNodes.length > 1) {
      const heirsHorizontalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      const firstHeirX = heirNodes[0].x;
      const lastHeirX = heirNodes[heirNodes.length - 1].x;
      heirsHorizontalLine.setAttribute('x1', firstHeirX);
      heirsHorizontalLine.setAttribute('y1', heirsLineY);
      heirsHorizontalLine.setAttribute('x2', lastHeirX);
      heirsHorizontalLine.setAttribute('y2', heirsLineY);
      heirsHorizontalLine.setAttribute('stroke', '#6a3e21');
      heirsHorizontalLine.setAttribute('stroke-width', '1');
      linesGroup.appendChild(heirsHorizontalLine);
    }

    // 5. 相続人への縦線
    heirNodes.forEach(heir => {
      const heirVerticalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      heirVerticalLine.setAttribute('x1', heir.x);
      heirVerticalLine.setAttribute('y1', heirsLineY);
      heirVerticalLine.setAttribute('x2', heir.x);
      heirVerticalLine.setAttribute('y2', heir.y - iconHeight / 2 - lineIconGap);
      heirVerticalLine.setAttribute('stroke', '#6a3e21');
      heirVerticalLine.setAttribute('stroke-width', '1');
      linesGroup.appendChild(heirVerticalLine);
    });

    // 6. 孫への線（第1順位のみ）
    if (activeRank === 1) {
      familyData.children.forEach((child, i) => {
        if (child.grandchildren > 0) {
          const childNode = heirNodes[i];
          const grandchild = nodes.find(n => n.type === 'grandchild' && n.parentIndex === i);

          if (grandchild && childNode) {
            const childVerticalLine = document.createElementNS('http://www.w3.org/2000/svg', 'line');
            childVerticalLine.setAttribute('x1', childNode.x);
            childVerticalLine.setAttribute('y1', childNode.y + iconHeight / 2 + fontSize + textBottomGap);
            childVerticalLine.setAttribute('x2', childNode.x);
            childVerticalLine.setAttribute('y2', grandchild.y - iconHeight / 2 - lineIconGap);
            childVerticalLine.setAttribute('stroke', '#6a3e21');
            childVerticalLine.setAttribute('stroke-width', '1');
            linesGroup.appendChild(childVerticalLine);
          }
        }
      });
    }
  }

  // ノード描画（人型アイコン + テキスト）
  for (const node of nodes) {
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');

    // テキスト色の決定（被相続人は故人扱いだが、配偶者・子・孫は状態で判定）
    const textColor = (node.type === 'deceased' || node.status === 'deceased')
      ? TEXT_COLORS.DECEASED
      : TEXT_COLORS.ALIVE;

    // アイコンURLを取得
    const iconUrl = ICON_URLS[node.type];

    // PNGアイコンを読み込み（Base64データURL）
    const iconDataUrl = await loadPNGIcon(iconUrl);

    // 人型アイコン（<image>タグで描画）
    const iconImage = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    iconImage.setAttribute('x', node.x - iconWidth / 2);
    iconImage.setAttribute('y', node.y - iconHeight / 2);
    iconImage.setAttribute('width', iconWidth);
    iconImage.setAttribute('height', iconHeight);
    iconImage.setAttribute('href', iconDataUrl);

    // グレースケールフィルターを適用（故人の場合のみ、被相続人は除く）
    if (node.status === 'deceased' && node.type !== 'deceased') {
      // グレースケールフィルターを定義（一度だけ）
      if (!svg.querySelector('#grayscaleFilter')) {
        const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
        defs.innerHTML = `
          <filter id="grayscaleFilter">
            <feColorMatrix type="saturate" values="0"/>
          </filter>
        `;
        svg.appendChild(defs);
      }
      iconImage.setAttribute('filter', 'url(#grayscaleFilter)');
      iconImage.setAttribute('opacity', '0.6');
    }

    g.appendChild(iconImage);

    // テキスト（アイコンの下）- 名前と相続割合
    const textY = node.y + iconHeight / 2 + fontSize + textBottomGap;

    // 相続割合を取得
    const ratio = showRatios && node.ratio ? node.ratio : null;

    if (ratio) {
      // 相続割合がある場合: 名前と割合を横並びで表示
      // 名前（左側）
      const nameText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      nameText.setAttribute('x', node.x);
      nameText.setAttribute('y', textY);
      nameText.setAttribute('text-anchor', 'end');  // 右寄せ
      nameText.setAttribute('fill', textColor);
      nameText.setAttribute('font-weight', 'bold');
      nameText.setAttribute('font-size', fontSize);
      nameText.textContent = node.label;
      g.appendChild(nameText);

      // 相続割合（右側）
      const ratioText = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      ratioText.setAttribute('x', node.x + fontSize * 0.3);  // 名前の右に少し間隔を空ける
      ratioText.setAttribute('y', textY);
      ratioText.setAttribute('text-anchor', 'start');  // 左寄せ
      ratioText.setAttribute('fill', textColor);  // 名前と同じ色
      ratioText.setAttribute('font-size', ratioFontSize);
      ratioText.textContent = `(${ratio})`;
      g.appendChild(ratioText);
    } else {
      // 相続割合がない場合: 名前のみ中央揃え
      const text = document.createElementNS('http://www.w3.org/2000/svg', 'text');
      text.setAttribute('x', node.x);
      text.setAttribute('y', textY);
      text.setAttribute('text-anchor', 'middle');
      text.setAttribute('fill', textColor);
      text.setAttribute('font-weight', 'bold');
      text.setAttribute('font-size', fontSize);
      text.textContent = node.label;
      g.appendChild(text);
    }

    svg.appendChild(g);
  }

  // 線を最後に追加（ノードの下に表示）
  svg.appendChild(linesGroup);

  return svg;
}
